(function () {
  'use strict';

  if (window.UnifiedRulesEngine) {
    return;
  }

  const VERSION = '1.1.0';

  const VALID_DECISIONS = [
    'allow',
    'block'
  ];

  /*
   * ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------
   */

  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }

  function getRulesConfig() {
    const config =
      getConfig();

    return (
      config.rules ||
      {}
    );
  }

  function getSchema() {
    return (
      window.UnifiedSchema ||
      null
    );
  }

  function isDebugEnabled() {
    return (
      getConfig().debug === true
    );
  }

  function isPlainObject(value) {
    return Boolean(
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    );
  }

  function clone(value) {
    if (
      typeof value ===
      'undefined'
    ) {
      return undefined;
    }

    try {
      return JSON.parse(
        JSON.stringify(value)
      );
    } catch (error) {
      return value;
    }
  }

  /*
   * ------------------------------------------------------------
   * PERFORMANCE
   * ------------------------------------------------------------
   */

  function performanceStart(
    label,
    meta
  ) {
    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance.start ===
        'function'
    ) {
      return window.UnifiedPerformance.start(
        label,
        meta || {}
      );
    }

    return null;
  }

  function performanceEnd(
    tokenId,
    meta
  ) {
    if (
      tokenId === null ||
      typeof tokenId ===
        'undefined'
    ) {
      return null;
    }

    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance.end ===
        'function'
    ) {
      return window.UnifiedPerformance.end(
        tokenId,
        meta || {}
      );
    }

    return null;
  }

  /*
   * ------------------------------------------------------------
   * ERROR REPORTING
   * ------------------------------------------------------------
   */

  function reportError(
    severity,
    code,
    message,
    details
  ) {
    if (
      window.UnifiedErrors &&
      typeof window.UnifiedErrors.report ===
        'function'
    ) {
      try {
        return window.UnifiedErrors.report(
          severity,
          code,
          message,
          details || {}
        );
      } catch (error) {
        // Console fallback below.
      }
    }

    const method =
      severity === 'warning'
        ? 'warn'
        : 'error';

    console[method](
      '[Unified Rules Engine] ' +
        message,
      details || {}
    );

    return null;
  }

  /*
   * ------------------------------------------------------------
   * EVENT BUS
   * ------------------------------------------------------------
   */

  function emit(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      try {
        return window.UnifiedBus.emit(
          type,
          data || {}
        );
      } catch (error) {
        reportError(
          'warning',
          'rules_bus_emit_failed',
          'Rules Engine could not emit through Unified Bus.',
          {
            event_type:
              type,
            error:
              error.message
          }
        );
      }
    }

    try {
      window.dispatchEvent(
        new CustomEvent(
          'unified:debug',
          {
            detail: {
              type:
                type,

              timestamp:
                new Date()
                  .toISOString(),

              data:
                data || {}
            }
          }
        )
      );
    } catch (error) {
      // Best effort only.
    }

    return null;
  }

  /*
   * ------------------------------------------------------------
   * RULE MATCHING
   * ------------------------------------------------------------
   */

  function valueMatches(
    actual,
    condition
  ) {
    /*
     * Primitive condition:
     * exact match.
     */

    if (
      !isPlainObject(
        condition
      )
    ) {
      return (
        actual ===
        condition
      );
    }

    if (
      Object.prototype
        .hasOwnProperty.call(
          condition,
          'equals'
        )
    ) {
      if (
        actual !==
        condition.equals
      ) {
        return false;
      }
    }

    if (
      Object.prototype
        .hasOwnProperty.call(
          condition,
          'not_equals'
        )
    ) {
      if (
        actual ===
        condition.not_equals
      ) {
        return false;
      }
    }

    if (
      Array.isArray(
        condition.in
      )
    ) {
      if (
        !condition.in.includes(
          actual
        )
      ) {
        return false;
      }
    }

    if (
      Array.isArray(
        condition.not_in
      )
    ) {
      if (
        condition.not_in.includes(
          actual
        )
      ) {
        return false;
      }
    }

    if (
      Object.prototype
        .hasOwnProperty.call(
          condition,
          'exists'
        )
    ) {
      const exists =
        typeof actual !==
          'undefined' &&
        actual !== null;

      if (
        exists !==
        Boolean(
          condition.exists
        )
      ) {
        return false;
      }
    }

    if (
      Object.prototype
        .hasOwnProperty.call(
          condition,
          'truthy'
        )
    ) {
      if (
        Boolean(actual) !==
        Boolean(
          condition.truthy
        )
      ) {
        return false;
      }
    }

    if (
      typeof condition.contains ===
        'string'
    ) {
      if (
        typeof actual !==
          'string' ||
        !actual.includes(
          condition.contains
        )
      ) {
        return false;
      }
    }

    return true;
  }

  function ruleMatches(
    rule,
    eventName,
    payload
  ) {
    if (
      !rule ||
      rule.enabled === false
    ) {
      return false;
    }

    const when =
      isPlainObject(
        rule.when
      )
        ? rule.when
        : {};

    if (
      when.event &&
      !valueMatches(
        eventName,
        when.event
      )
    ) {
      return false;
    }

    if (
      when.category &&
      !valueMatches(
        payload.event_category,
        when.category
      )
    ) {
      return false;
    }

    if (
      when.service &&
      !valueMatches(
        payload.service,
        when.service
      )
    ) {
      return false;
    }

    if (
      isPlainObject(
        when.fields
      )
    ) {
      const fieldNames =
        Object.keys(
          when.fields
        );

      for (
        let index = 0;
        index <
        fieldNames.length;
        index += 1
      ) {
        const fieldName =
          fieldNames[index];

        if (
          !valueMatches(
            payload[
              fieldName
            ],
            when.fields[
              fieldName
            ]
          )
        ) {
          return false;
        }
      }
    }

    return true;
  }

  /*
   * ------------------------------------------------------------
   * SAFE ENRICHMENT
   * ------------------------------------------------------------
   */

  function applyEnrichment(
    eventName,
    payload,
    fields,
    applied,
    warnings
  ) {
    if (
      !isPlainObject(
        fields
      )
    ) {
      return payload;
    }

    const schema =
      getSchema();

    if (
      !schema ||
      typeof schema.getAllowedFields !==
        'function'
    ) {
      warnings.push({
        code:
          'enrichment_schema_unavailable'
      });

      return payload;
    }

    const allowedFields =
      new Set(
        schema.getAllowedFields(
          eventName
        )
      );

    const result =
      Object.assign(
        {},
        payload
      );

    Object.keys(fields)
      .forEach(
        function (
          fieldName
        ) {
          /*
           * Rules Engine may never create
           * a field outside Schema.
           */

          if (
            !allowedFields.has(
              fieldName
            )
          ) {
            warnings.push({
              code:
                'enrichment_field_blocked',

              field:
                fieldName
            });

            return;
          }

          const definition =
            typeof schema.getFieldDefinition ===
              'function'
              ? schema.getFieldDefinition(
                  eventName,
                  fieldName
                )
              : null;

          /*
           * Sensitive fields can never
           * be injected by a rule.
           */

          if (
            definition &&
            definition.analytics ===
              'excluded_sensitive'
          ) {
            warnings.push({
              code:
                'enrichment_sensitive_field_blocked',

              field:
                fieldName
            });

            return;
          }

          const beforeValue =
            result[
              fieldName
            ];

          result[
            fieldName
          ] =
            clone(
              fields[
                fieldName
              ]
            );

          applied.push({
            type:
              'enrich',

            field:
              fieldName,

            changed:
              beforeValue !==
              result[
                fieldName
              ]
          });
        }
      );

    return result;
  }

  /*
   * ------------------------------------------------------------
   * ROUTING
   * ------------------------------------------------------------
   */

  function applyRouting(
    providers,
    routes,
    warnings
  ) {
    if (
      !Array.isArray(
        providers
      )
    ) {
      return;
    }

    const configuredProviders =
      getConfig().providers ||
      {};

    providers.forEach(
      function (
        provider
      ) {
        if (
          typeof provider !==
            'string' ||
          !provider.trim()
        ) {
          return;
        }

        const normalized =
          provider
            .trim()
            .toLowerCase();

        if (
          !Object.prototype
            .hasOwnProperty.call(
              configuredProviders,
              normalized
            )
        ) {
          warnings.push({
            code:
              'route_provider_unknown',

            provider:
              normalized
          });

          return;
        }

        if (
          !routes.includes(
            normalized
          )
        ) {
          routes.push(
            normalized
          );
        }
      }
    );
  }

  /*
   * ------------------------------------------------------------
   * ACTION PROCESSING
   * ------------------------------------------------------------
   */

  function normalizeActions(
    rule
  ) {
    if (
      Array.isArray(
        rule.actions
      )
    ) {
      return rule.actions;
    }

    /*
     * Convenience format:
     *
     * action: 'block'
     */

    if (
      typeof rule.action ===
        'string'
    ) {
      return [
        {
          type:
            rule.action,

          reason:
            rule.reason
        }
      ];
    }

    return [];
  }

  function applyAction(
    action,
    state,
    eventName
  ) {
    if (
      !action ||
      typeof action.type !==
        'string'
    ) {
      return;
    }

    const type =
      action.type
        .trim()
        .toLowerCase();

    switch (type) {
      case 'allow':
        state.decision =
          'allow';

        state.reason =
          action.reason ||
          state.reason;

        state.actions.push({
          type:
            'allow'
        });

        break;

      case 'block':
        state.decision =
          'block';

        state.reason =
          action.reason ||
          'blocked_by_rule';

        state.actions.push({
          type:
            'block',

          reason:
            state.reason
        });

        break;

      case 'enrich':
        state.payload =
          applyEnrichment(
            eventName,
            state.payload,
            action.fields,
            state.actions,
            state.warnings
          );

        break;

      case 'route':
        applyRouting(
          action.providers,
          state.routes,
          state.warnings
        );

        state.actions.push({
          type:
            'route',

          providers:
            clone(
              state.routes
            )
        });

        break;

      default:
        state.warnings.push({
          code:
            'rule_action_unknown',

          action:
            type
        });
    }
  }

  /*
   * ------------------------------------------------------------
   * RULE PREPARATION
   * ------------------------------------------------------------
   */

  function getRules() {
    const rulesConfig =
      getRulesConfig();

    const definitions =
      Array.isArray(
        rulesConfig.definitions
      )
        ? rulesConfig.definitions
        : [];

    return definitions
      .filter(
        function (
          rule
        ) {
          return (
            rule &&
            rule.enabled !==
              false
          );
        }
      )
      .slice()
      .sort(
        function (
          first,
          second
        ) {
          const firstPriority =
            Number.isFinite(
              Number(
                first.priority
              )
            )
              ? Number(
                  first.priority
                )
              : 100;

          const secondPriority =
            Number.isFinite(
              Number(
                second.priority
              )
            )
              ? Number(
                  second.priority
                )
              : 100;

          return (
            firstPriority -
            secondPriority
          );
        }
      );
  }

  function getDefaultDecision() {
    const rulesConfig =
      getRulesConfig();

    const candidate =
      String(
        rulesConfig.defaultDecision ||
        'allow'
      )
        .trim()
        .toLowerCase();

    if (
      VALID_DECISIONS.includes(
        candidate
      )
    ) {
      return candidate;
    }

    return 'allow';
  }

  /*
   * ------------------------------------------------------------
   * MAIN EVALUATION
   * ------------------------------------------------------------
   */

  function evaluate(
    eventName,
    payload
  ) {
    const performanceToken =
      performanceStart(
        'rules.total',
        {
          event:
            eventName
        }
      );

    if (
      typeof eventName !==
        'string' ||
      !eventName.trim() ||
      !isPlainObject(
        payload
      )
    ) {
      const failure = {
        success:
          false,

        event:
          eventName,

        decision:
          'block',

        allowed:
          false,

        blocked:
          true,

        reason:
          'rules_input_invalid',

        payload:
          isPlainObject(
            payload
          )
            ? clone(payload)
            : {},

        routes:
          [],

        matched_rules:
          [],

        actions:
          [],

        warnings:
          [],

        errors: [
          {
            code:
              'rules_input_invalid'
          }
        ]
      };

      reportError(
        'warning',
        'rules_input_invalid',
        'Rules Engine received an invalid event or payload.',
        {
          event:
            eventName
        }
      );

      performanceEnd(
        performanceToken,
        {
          status:
            'blocked',

          reason:
            failure.reason
        }
      );

      return failure;
    }

    const rulesConfig =
      getRulesConfig();

    /*
     * Rules disabled =
     * allow without evaluating definitions.
     */

    if (
      rulesConfig.enabled ===
      false
    ) {
      const result = {
        success:
          true,

        event:
          eventName,

        decision:
          'allow',

        allowed:
          true,

        blocked:
          false,

        reason:
          'rules_disabled',

        payload:
          clone(payload),

        routes:
          [],

        matched_rules:
          [],

        actions:
          [],

        warnings:
          [],

        errors:
          []
      };

      emit(
        'event_rules_evaluated',
        result
      );

      performanceEnd(
        performanceToken,
        {
          event:
            eventName,

          status:
            'success',

          decision:
            'allow'
        }
      );

      return result;
    }

    const state = {
      decision:
        getDefaultDecision(),

      reason:
        'default_decision',

      payload:
        clone(payload),

      routes:
        [],

      matched_rules:
        [],

      actions:
        [],

      warnings:
        []
    };

    const rules =
      getRules();

    rules.forEach(
      function (
        rule,
        index
      ) {
        if (
          !ruleMatches(
            rule,
            eventName,
            state.payload
          )
        ) {
          return;
        }

        const ruleId =
          rule.id ||
          (
            'rule_' +
            String(index + 1)
          );

        state.matched_rules.push(
          ruleId
        );

        const actions =
          normalizeActions(
            rule
          );

        actions.forEach(
          function (
            action
          ) {
            applyAction(
              action,
              state,
              eventName
            );
          }
        );
      }
    );

    const allowed =
      state.decision ===
      'allow';

    const result = {
      success:
        true,

      event:
        eventName,

      decision:
        state.decision,

      allowed:
        allowed,

      blocked:
        !allowed,

      reason:
        state.reason,

      payload:
        state.payload,

      routes:
        state.routes,

      matched_rules:
        state.matched_rules,

      actions:
        state.actions,

      warnings:
        state.warnings,

      errors:
        []
    };

    emit(
      'event_rules_evaluated',
      {
        event:
          eventName,

        decision:
          result.decision,

        allowed:
          result.allowed,

        blocked:
          result.blocked,

        reason:
          result.reason,

        routes:
          result.routes,

        matched_rules:
          result.matched_rules,

        actions:
          result.actions,

        warnings:
          result.warnings
      }
    );

    emit(
      allowed
        ? 'event_allowed'
        : 'event_blocked',
      {
        event:
          eventName,

        reason:
          result.reason,

        routes:
          result.routes,

        matched_rules:
          result.matched_rules
      }
    );

    performanceEnd(
      performanceToken,
      {
        event:
          eventName,

        status:
          allowed
            ? 'allowed'
            : 'blocked',

        matched_rules:
          result.matched_rules
            .length,

        warnings:
          result.warnings
            .length
      }
    );

    if (
      isDebugEnabled()
    ) {
      console.info(
        '[Unified Rules Engine] Evaluation completed',
        {
          event:
            eventName,

          decision:
            result.decision,

          matchedRules:
            result.matched_rules,

          routes:
            result.routes,

          warnings:
            result.warnings.length
        }
      );
    }

    return result;
  }

  /*
   * ------------------------------------------------------------
   * PUBLIC API
   * ------------------------------------------------------------
   */

  window.UnifiedRulesEngine = {
    version:
      VERSION,

    evaluate:
      evaluate,

    getRules:
      function () {
        return clone(
          getRules()
        );
      },

    getDefaultDecision:
      getDefaultDecision
  };

  if (
    isDebugEnabled()
  ) {
    console.info(
      '[Unified Rules Engine] Rules Engine initialized',
      {
        version:
          VERSION,

        enabled:
          getRulesConfig()
            .enabled !== false,

        defaultDecision:
          getDefaultDecision(),

        rules:
          getRules().length
      }
    );
  }
})();